import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { auth } from "../firebase/firebaseConfig";
import { listProducts, listCategories } from "../admin/productCatalog/productsService";
import { listSchools, matchUserSchool } from "../services/schoolsService";
import FirestoreProductCard from "./FirestoreProductCard";
import Navbar from "../containers/navbar/index";
import "./products-page.scss";
import "./school-spotlight.css";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [mySchool, setMySchool] = useState(null);

  useEffect(() => {
    Promise.all([listProducts(), listCategories()]).then(([prods, cats]) => {
      setProducts(prods.filter((p) => p.active !== false));
      setCategories(cats);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // "/" already knows how to send a signed-in user to the right
    // dashboard (student/educator/admin) — reusing that instead of
    // duplicating the role logic here.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsSignedIn(!!user);
      if (!user) return setMySchool(null);

      // Match the signed-in buyer to a school so their own school's items can
      // be surfaced first. Nothing is hidden from anyone — this only changes
      // ordering, and the picker still offers every approved school.
      try {
        const profileSnap = await getDoc(doc(getFirestore(), "users", user.uid));
        if (!profileSnap.exists()) return;
        const p = profileSnap.data();
        const schools = await listSchools({ approvedOnly: true });
        setMySchool(matchUserSchool(schools, p.state, p.schoolName));
      } catch (err) {
        console.error("Could not match user school:", err);
      }
    });
    return unsubscribe;
  }, []);

  const term = searchTerm.trim().toLowerCase();

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory = activeCategory === "all" || p.categoryId === activeCategory;
      const matchesSearch =
        !term ||
        p.name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [products, activeCategory, term]);

  // Personalized items shown twice would be confusing, so when we know the
  // buyer's school those cards move into a pinned section at the top,
  // pre-filled with their school, and come out of the main grid.
  const schoolProducts = useMemo(
    () => (mySchool ? filtered.filter((p) => p.requiresSchool === true) : []),
    [filtered, mySchool]
  );

  const restProducts = useMemo(
    () =>
      mySchool ? filtered.filter((p) => p.requiresSchool !== true) : filtered,
    [filtered, mySchool]
  );

  return (
    <main className="products-page-full">
      <Navbar />

      <div className="products-page-header">
        <h1>All Products</h1>
        <div className="products-header-actions">
          {isSignedIn && (
            <a href="/" className="back-to-portal-link">← Back to My Portal</a>
          )}
          <input
            type="text"
            className="products-search"
            placeholder="Search products…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="category-tabs">
        <button
          className={`category-tab ${activeCategory === "all" ? "active" : ""}`}
          onClick={() => setActiveCategory("all")}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`category-tab ${activeCategory === cat.id ? "active" : ""}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.image && <img src={cat.image} alt="" />}
            {cat.name}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="products-loading">Loading products…</p>
      ) : filtered.length === 0 ? (
        <p className="products-empty">No products found.</p>
      ) : (
        <>
          {schoolProducts.length > 0 && (
            <section className="school-spotlight">
              <div className="school-spotlight-head">
                {mySchool.logoUrl && (
                  <img src={mySchool.logoUrl} alt={`${mySchool.name} logo`} />
                )}
                <div>
                  <h2>For {mySchool.name}</h2>
                  <p>
                    Personalized with your school. You can pick a different
                    school on any item.
                  </p>
                </div>
              </div>

              <div className="products-grid">
                {schoolProducts.map((p) => (
                  <FirestoreProductCard
                    key={`school-${p.id}`}
                    product={p}
                    defaultSchool={mySchool}
                  />
                ))}
              </div>
            </section>
          )}

          <div className="products-grid">
            {restProducts.map((p) => (
              <FirestoreProductCard key={p.id} product={p} />
            ))}
          </div>
        </>
      )}
    </main>
  );
}
